package org.example.project.screens_cus_mobile

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.example.project.engineletter.LETTER_LAYOUT_DOKUMENTATION_WA
import org.example.project.engineletter.LETTER_LAYOUT_DOKUMENTATION_WE
import org.example.project.engineletter.LETTER_LAYOUT_QUALITAET_WA
import org.example.project.engineletter.LETTER_LAYOUT_QUALITAET_WE
import org.example.project.engineletter.LETTER_LAYOUT_QUALITAET_WE_BERICHT
import org.example.project.enginekartei.KarteiApi
import org.example.project.engineletter.LETTER_LAYOUT_PALSCHEIN_WA
import org.example.project.engineletter.LETTER_LAYOUT_PALSCHEIN_WE
import org.example.project.engineletter.RenderYardLetter
import org.example.project.engineletter.renderLetterDocumentPdfFile
import org.example.project.engineservice.ServiceApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.LoginUserApi
import org.example.project.service.YardApi
import org.example.project.service.YardSettingsService
import org.example.project.service.YardDocumentSnapshot
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.left
import org.jetbrains.compose.web.css.position
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.top
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement
import kotlin.coroutines.resume

private const val MOBILE_YARD_PALSCHEIN_WE_ROOT = "mobile-yard-palschein-we-root"
private const val MOBILE_YARD_PALSCHEIN_WA_ROOT = "mobile-yard-palschein-wa-root"
private const val MOBILE_YARD_DOKUMENTATION_WE_ROOT = "mobile-yard-dokumentation-we-root"
private const val MOBILE_YARD_DOKUMENTATION_WA_ROOT = "mobile-yard-dokumentation-wa-root"
private const val MOBILE_YARD_QUALITAET_WE_ROOT = "mobile-yard-qualitaet-we-root"
private const val MOBILE_YARD_QUALITAET_WA_ROOT = "mobile-yard-qualitaet-wa-root"
private const val MOBILE_YARD_WE_BERICHT_ROOT = "mobile-yard-we-bericht-root"
private const val MOBILE_YARD_PALSCHEIN_RENDER_ATTEMPTS = 120
private const val MOBILE_YARD_DOCUMENT_PHASE_DELAY_MS = 1100L
private const val MOBILE_YARD_DOCUMENT_UPLOAD_SETTLE_MS = 450L
const val MOBILE_YARD_STORED_PDF_ENABLED = false

@Composable
fun MobileYardPalettenscheinExportHost(
    art: String,
    auftrag: All?,
    onPhaseChange: (String) -> Unit = {},
    onDocumentProgress: (Int) -> Unit = {},
    onPalettenscheinUploaded: suspend (All) -> All? = { it },
    onFinished: (Boolean) -> Unit
) {
    var unternehmen by remember { mutableStateOf(All()) }
    var logoSrc by remember { mutableStateOf("") }
    var qualitaetLogoSrc by remember { mutableStateOf("") }
    var vorgangslog by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontrollDefinitionen by remember { mutableStateOf<List<All>>(emptyList()) }
    var settingsLoaded by remember { mutableStateOf(false) }
    var runningExportKey by remember { mutableStateOf("") }
    var renderAuftrag by remember { mutableStateOf<All?>(null) }
    var dokumentationAuftrag by remember { mutableStateOf<All?>(null) }
    val normalizedArt = art.uppercase()
    val palscheinRootId = if (normalizedArt == "WE") MOBILE_YARD_PALSCHEIN_WE_ROOT else MOBILE_YARD_PALSCHEIN_WA_ROOT
    val dokumentationRootId = if (normalizedArt == "WE") MOBILE_YARD_DOKUMENTATION_WE_ROOT else MOBILE_YARD_DOKUMENTATION_WA_ROOT
    val qualitaetRootId = if (normalizedArt == "WE") MOBILE_YARD_QUALITAET_WE_ROOT else MOBILE_YARD_QUALITAET_WA_ROOT
    val layoutKey = if (normalizedArt == "WE") LETTER_LAYOUT_PALSCHEIN_WE else LETTER_LAYOUT_PALSCHEIN_WA
    val dokumentationLayoutKey = if (normalizedArt == "WE") LETTER_LAYOUT_DOKUMENTATION_WE else LETTER_LAYOUT_DOKUMENTATION_WA
    val qualitaetLayoutKey = if (normalizedArt == "WE") LETTER_LAYOUT_QUALITAET_WE else LETTER_LAYOUT_QUALITAET_WA
    val mobileScreenKey = if (normalizedArt == "WE") "MobileWEAuswahlScreen" else "MobileWAAuswahlScreen"

    LaunchedEffect(Unit) {
        val palscheinLogo = YardSettingsService.logoForLayout(layoutKey)
        logoSrc = palscheinLogo
        qualitaetLogoSrc = YardSettingsService.logoForLayout(qualitaetLayoutKey).ifBlank { palscheinLogo }
        unternehmen = KarteiApi.loadUnternehmen() ?: All()
        settingsLoaded = true
    }

    LaunchedEffect(
        auftrag?.lieferscheinnummer,
        auftrag?.id,
        auftrag?.lfdNr,
        auftrag?.waStandortId,
        auftrag?.slotStandortId,
        settingsLoaded
    ) {
        val source = auftrag ?: return@LaunchedEffect
        if (!settingsLoaded) return@LaunchedEffect
        val lieferscheinnummer = source.lieferscheinnummer.ifBlank { return@LaunchedEffect }
        val exportKey = listOf(
            normalizedArt,
            lieferscheinnummer,
            source.id,
            source.lfdNr.toString(),
            mobileYardDocumentStandortKey(source)
        ).joinToString(":")
        if (runningExportKey == exportKey) return@LaunchedEffect
        runningExportKey = exportKey
        val renderAbschlussTimestamp = kotlin.js.Date().getTime().toLong()
        val documentSource = mobileYardDocumentSource(normalizedArt, source)
        val dokumentationSource = mobileYardDocumentAbschlussSource(documentSource, renderAbschlussTimestamp)
        renderAuftrag = documentSource
        dokumentationAuftrag = dokumentationSource
        vorgangslog = emptyList()
        kontrollDefinitionen = YardApi.loadKontrolleDefinitionen().filter {
            if (normalizedArt == "WE") it.aktivWe else it.aktivWa
        }
        if (!MOBILE_YARD_STORED_PDF_ENABLED) {
            val success = runCatching {
                val type = if (normalizedArt == "WE") "yard-we" else "yard-wa"
                val loadedVorgangslog = if (normalizedArt == "WE") {
                    YardApi.loadWeVorgangslog(documentSource.lfdNr.takeIf { it > 0 } ?: documentSource.id.toIntOrNull() ?: 0, documentSource.firmenId)
                } else {
                    YardApi.loadWaVorgangslog(documentSource.lieferscheinnummer.ifBlank { lieferscheinnummer }, documentSource.firmenId)
                }
                vorgangslog = mobileYardDocumentVorgangslog(
                    normalizedArt = normalizedArt,
                    auftrag = dokumentationSource,
                    rows = loadedVorgangslog,
                    timestamp = renderAbschlussTimestamp
                )
                onPhaseChange("Auftragsdaten werden gespeichert")
                onDocumentProgress(25)
                val palscheinOk = mobileYardSaveDocumentSnapshot(
                    type = type,
                    entryId = lieferscheinnummer,
                    pdfFileName = mobileYardPalettenscheinFileName(documentSource),
                    dokumentTyp = "palettenschein",
                    layoutKey = layoutKey,
                    auftrag = documentSource,
                    unternehmen = unternehmen,
                    logoSrc = logoSrc
                )
                if (!palscheinOk) return@runCatching false
                onDocumentProgress(50)
                val dokumentationOk = mobileYardSaveDocumentSnapshot(
                    type = type,
                    entryId = lieferscheinnummer,
                    pdfFileName = mobileYardProzessdokumentationFileName(documentSource),
                    dokumentTyp = "prozessdokumentation",
                    layoutKey = dokumentationLayoutKey,
                    auftrag = dokumentationSource,
                    unternehmen = unternehmen,
                    vorgangslog = vorgangslog,
                    kontrollDefinitionen = kontrollDefinitionen,
                    logoSrc = logoSrc
                )
                if (!dokumentationOk) return@runCatching false
                onDocumentProgress(75)
                val qualitaetOk = mobileYardSaveDocumentSnapshot(
                    type = type,
                    entryId = lieferscheinnummer,
                    pdfFileName = mobileYardQualitaetsdokumentationFileName(documentSource),
                    dokumentTyp = "qualitaetsdokumentation",
                    layoutKey = qualitaetLayoutKey,
                    auftrag = dokumentationSource,
                    unternehmen = unternehmen,
                    kontrollDefinitionen = kontrollDefinitionen,
                    logoSrc = qualitaetLogoSrc.ifBlank { logoSrc }
                )
                if (!qualitaetOk) return@runCatching false
                val savedAuftrag = onPalettenscheinUploaded(documentSource) ?: return@runCatching false
                dokumentationAuftrag = savedAuftrag
                onPhaseChange("Auftrag abgeschlossen")
                onDocumentProgress(100)
                true
            }.onFailure {
                console.error("Mobile Yard snapshot save failed", normalizedArt, lieferscheinnummer, it)
            }.getOrDefault(false)
            onFinished(success)
            return@LaunchedEffect
        }
        delay(75)
        onPhaseChange("Palettenschein wird erstellt")
        onDocumentProgress(8)
        mobileYardWaitForDocumentRenderSettle()
        val palscheinRoot = waitForMobileYardPalettenscheinRoot(palscheinRootId) ?: run {
            console.error("Mobile Yard Palettenschein PDF export failed: document root missing", normalizedArt, lieferscheinnummer)
            onFinished(false)
            return@LaunchedEffect
        }
        if (!waitForMobileYardPalettenscheinReady(palscheinRoot)) {
            console.error("Mobile Yard Palettenschein PDF export failed: document still loading", normalizedArt, lieferscheinnummer)
            onFinished(false)
            return@LaunchedEffect
        }
        val success = runCatching {
            val exportSource = renderAuftrag ?: documentSource
            val fileName = mobileYardPalettenscheinFileName(exportSource)
            val type = if (normalizedArt == "WE") "yard-we" else "yard-wa"
            val dokumentationExportSource = mobileYardDocumentAbschlussSource(exportSource, renderAbschlussTimestamp)
            GlobalManager.markLocalScreenWrite(mobileScreenKey, suppressMs = 60000L)
            val palscheinOk = mobileYardRenderAndUploadDocument(
                root = palscheinRoot,
                fileName = fileName,
                type = type,
                entryId = lieferscheinnummer,
                errorContext = "Palettenschein",
                normalizedArt = normalizedArt
            )
            if (!palscheinOk) return@runCatching false
            val palscheinSnapshotOk = mobileYardSaveDocumentSnapshot(
                type = type,
                entryId = lieferscheinnummer,
                pdfFileName = fileName,
                dokumentTyp = "palettenschein",
                layoutKey = layoutKey,
                auftrag = exportSource,
                unternehmen = unternehmen,
                logoSrc = logoSrc
            )
            if (!palscheinSnapshotOk) return@runCatching false
            onDocumentProgress(30)
            delay(MOBILE_YARD_DOCUMENT_UPLOAD_SETTLE_MS)
            dokumentationAuftrag = dokumentationExportSource
            val loadedVorgangslog = if (normalizedArt == "WE") {
                YardApi.loadWeVorgangslog(exportSource.lfdNr.takeIf { it > 0 } ?: exportSource.id.toIntOrNull() ?: 0, exportSource.firmenId)
            } else {
                YardApi.loadWaVorgangslog(exportSource.lieferscheinnummer.ifBlank { lieferscheinnummer }, exportSource.firmenId)
            }
            vorgangslog = mobileYardDocumentVorgangslog(
                normalizedArt = normalizedArt,
                auftrag = dokumentationExportSource,
                rows = loadedVorgangslog,
                timestamp = renderAbschlussTimestamp
            )
            onPhaseChange("Prozessdokumentation wird erstellt")
            onDocumentProgress(40)
            mobileYardWaitForDocumentRenderSettle()
            GlobalManager.markLocalScreenWrite(mobileScreenKey, suppressMs = 60000L)
            val dokumentationRoot = waitForMobileYardPalettenscheinRoot(dokumentationRootId) ?: run {
                console.error("Mobile Yard Prozessdokumentation PDF export failed: document root missing", normalizedArt, lieferscheinnummer)
                return@runCatching false
            }
            if (!waitForMobileYardPalettenscheinReady(dokumentationRoot)) {
                console.error("Mobile Yard Prozessdokumentation PDF export failed: document still loading", normalizedArt, lieferscheinnummer)
                return@runCatching false
            }
            val dokumentationFileName = mobileYardProzessdokumentationFileName(exportSource)
            val dokumentationOk = mobileYardRenderAndUploadDocument(
                root = dokumentationRoot,
                fileName = dokumentationFileName,
                type = type,
                entryId = lieferscheinnummer,
                errorContext = "Prozessdokumentation",
                normalizedArt = normalizedArt
            )
            if (!dokumentationOk) return@runCatching false
            val dokumentationSnapshotOk = mobileYardSaveDocumentSnapshot(
                type = type,
                entryId = lieferscheinnummer,
                pdfFileName = dokumentationFileName,
                dokumentTyp = "prozessdokumentation",
                layoutKey = dokumentationLayoutKey,
                auftrag = dokumentationExportSource,
                unternehmen = unternehmen,
                vorgangslog = vorgangslog,
                kontrollDefinitionen = kontrollDefinitionen,
                logoSrc = logoSrc
            )
            if (!dokumentationSnapshotOk) return@runCatching false
            onDocumentProgress(66)
            delay(MOBILE_YARD_DOCUMENT_UPLOAD_SETTLE_MS)
            onPhaseChange("Qualitätsdokumentation wird erstellt")
            onDocumentProgress(72)
            mobileYardWaitForDocumentRenderSettle()
            GlobalManager.markLocalScreenWrite(mobileScreenKey, suppressMs = 60000L)
            val qualitaetRoot = waitForMobileYardPalettenscheinRoot(qualitaetRootId) ?: run {
                console.error("Mobile Yard Qualitätsdokumentation PDF export failed: document root missing", normalizedArt, lieferscheinnummer)
                return@runCatching false
            }
            if (!waitForMobileYardPalettenscheinReady(qualitaetRoot)) {
                console.error("Mobile Yard Qualitätsdokumentation PDF export failed: document still loading", normalizedArt, lieferscheinnummer)
                return@runCatching false
            }
            val qualitaetFileName = mobileYardQualitaetsdokumentationFileName(exportSource)
            val qualitaetOk = mobileYardRenderAndUploadDocument(
                root = qualitaetRoot,
                fileName = qualitaetFileName,
                type = type,
                entryId = lieferscheinnummer,
                errorContext = "Qualitätsdokumentation",
                normalizedArt = normalizedArt
            )
            if (!qualitaetOk) return@runCatching false
            val qualitaetSnapshotOk = mobileYardSaveDocumentSnapshot(
                type = type,
                entryId = lieferscheinnummer,
                pdfFileName = qualitaetFileName,
                dokumentTyp = "qualitaetsdokumentation",
                layoutKey = qualitaetLayoutKey,
                auftrag = dokumentationExportSource,
                unternehmen = unternehmen,
                kontrollDefinitionen = kontrollDefinitionen,
                logoSrc = qualitaetLogoSrc.ifBlank { logoSrc }
            )
            if (!qualitaetSnapshotOk) return@runCatching false
            onPhaseChange("Auftrag wird abgeschlossen")
            onDocumentProgress(96)
            val savedAuftrag = onPalettenscheinUploaded(exportSource) ?: return@runCatching false
            dokumentationAuftrag = savedAuftrag
            onPhaseChange("Dokumente wurden erzeugt.")
            onDocumentProgress(100)
            delay(MOBILE_YARD_DOCUMENT_PHASE_DELAY_MS)
            true
        }.onFailure {
            console.error("Mobile Yard Palettenschein PDF export failed", normalizedArt, lieferscheinnummer, it)
        }.getOrDefault(false)
        onFinished(success)
    }

    if (MOBILE_YARD_STORED_PDF_ENABLED && auftrag != null) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                property("opacity", "0")
                property("z-index", "-1")
                property("background", "#ffffff")
            }
        }) {
            Div({
                style {
                    width(794.px)
                    boxSizing("border-box")
                    borderRadius(8.px)
                }
            }) {
            val currentRenderAuftrag = renderAuftrag ?: auftrag
            RenderYardLetter(
                rootElementId = palscheinRootId,
                layoutKey = layoutKey,
                auftrag = currentRenderAuftrag,
                vorgangslog = emptyList(),
                unternehmen = unternehmen,
                logoSrc = logoSrc
            )
            RenderYardLetter(
                rootElementId = dokumentationRootId,
                layoutKey = dokumentationLayoutKey,
                auftrag = dokumentationAuftrag ?: auftrag,
                vorgangslog = vorgangslog,
                unternehmen = unternehmen,
                logoSrc = logoSrc,
                kontrollDefinitionen = kontrollDefinitionen
            )
            RenderYardLetter(
                rootElementId = qualitaetRootId,
                layoutKey = qualitaetLayoutKey,
                auftrag = dokumentationAuftrag ?: auftrag,
                vorgangslog = emptyList(),
                unternehmen = unternehmen,
                logoSrc = qualitaetLogoSrc.ifBlank { logoSrc },
                kontrollDefinitionen = kontrollDefinitionen
            )
            }
        }
    }
}

@Composable
fun MobileYardWEWareneingangsberichtExportHost(
    auftrag: All?,
    webKontrolle: All?,
    onPhaseChange: (String) -> Unit = {},
    onDocumentProgress: (Int) -> Unit = {},
    onFinished: (Boolean) -> Unit
) {
    var unternehmen by remember { mutableStateOf(All()) }
    var logoSrc by remember { mutableStateOf("") }
    var settingsLoaded by remember { mutableStateOf(false) }
    var runningExportKey by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        logoSrc = YardSettingsService.logoForLayout(LETTER_LAYOUT_QUALITAET_WE_BERICHT)
            .ifBlank { YardSettingsService.logoForLayout(LETTER_LAYOUT_QUALITAET_WE) }
        unternehmen = KarteiApi.loadUnternehmen() ?: All()
        settingsLoaded = true
    }

    LaunchedEffect(auftrag?.id, auftrag?.lfdNr, webKontrolle?.id, webKontrolle?.abgeschlossenAm, settingsLoaded) {
        val source = auftrag ?: return@LaunchedEffect
        val kontrolle = webKontrolle ?: return@LaunchedEffect
        if (!settingsLoaded) return@LaunchedEffect
        val lieferscheinnummer = source.lieferscheinnummer.ifBlank { kontrolle.lieferscheinnummer }.ifBlank { return@LaunchedEffect }
        val exportKey = listOf("WE_BERICHT", lieferscheinnummer, source.id, source.lfdNr.toString(), kontrolle.id).joinToString(":")
        if (runningExportKey == exportKey) return@LaunchedEffect
        runningExportKey = exportKey
        if (!MOBILE_YARD_STORED_PDF_ENABLED) {
            val success = mobileYardSaveDocumentSnapshot(
                type = "yard-we",
                entryId = lieferscheinnummer,
                pdfFileName = mobileYardWareneingangsberichtFileName(source),
                dokumentTyp = "we_fehlerprotokoll",
                layoutKey = LETTER_LAYOUT_QUALITAET_WE_BERICHT,
                auftrag = source,
                unternehmen = unternehmen,
                webKontrolle = kontrolle,
                logoSrc = logoSrc
            )
            onDocumentProgress(100)
            onFinished(success)
            return@LaunchedEffect
        }
        val success = runCatching {
            onPhaseChange("WE Fehlerprotokoll wird erstellt")
            onDocumentProgress(20)
            mobileYardWaitForDocumentRenderSettle()
            val root = waitForMobileYardPalettenscheinRoot(MOBILE_YARD_WE_BERICHT_ROOT) ?: run {
                console.error("Mobile Yard WE Fehlerprotokoll PDF export failed: document root missing", lieferscheinnummer)
                return@runCatching false
            }
            if (!waitForMobileYardPalettenscheinReady(root)) {
                console.error("Mobile Yard WE Fehlerprotokoll PDF export failed: document still loading", lieferscheinnummer)
                return@runCatching false
            }
            onDocumentProgress(70)
            val uploaded = mobileYardRenderAndUploadDocument(
                root = root,
                fileName = mobileYardWareneingangsberichtFileName(source),
                type = "yard-we",
                entryId = lieferscheinnummer,
                errorContext = "WE Fehlerprotokoll",
                normalizedArt = "WE"
            )
            if (!uploaded) return@runCatching false
            val snapshotOk = mobileYardSaveDocumentSnapshot(
                type = "yard-we",
                entryId = lieferscheinnummer,
                pdfFileName = mobileYardWareneingangsberichtFileName(source),
                dokumentTyp = "we_fehlerprotokoll",
                layoutKey = LETTER_LAYOUT_QUALITAET_WE_BERICHT,
                auftrag = source,
                unternehmen = unternehmen,
                webKontrolle = kontrolle,
                logoSrc = logoSrc
            )
            if (!snapshotOk) return@runCatching false
            onPhaseChange("WE Fehlerprotokoll wurde erzeugt.")
            onDocumentProgress(100)
            delay(MOBILE_YARD_DOCUMENT_PHASE_DELAY_MS)
            true
        }.onFailure {
            console.error("Mobile Yard WE Fehlerprotokoll PDF export failed", lieferscheinnummer, it)
        }.getOrDefault(false)
        onFinished(success)
    }

    if (MOBILE_YARD_STORED_PDF_ENABLED && auftrag != null && webKontrolle != null) {
        Div({
            style {
                position(Position.Fixed)
                left((-10000).px)
                top(0.px)
                width(794.px)
                property("pointer-events", "none")
                property("opacity", "0")
                property("z-index", "-1")
                property("background", "#ffffff")
            }
        }) {
            RenderYardLetter(
                rootElementId = MOBILE_YARD_WE_BERICHT_ROOT,
                layoutKey = LETTER_LAYOUT_QUALITAET_WE_BERICHT,
                auftrag = auftrag,
                vorgangslog = emptyList(),
                unternehmen = unternehmen,
                logoSrc = logoSrc,
                webKontrolle = webKontrolle
            )
        }
    }
}

private suspend fun mobileYardWaitForDocumentRenderSettle() {
    delay(MOBILE_YARD_DOCUMENT_PHASE_DELAY_MS)
    awaitMobileYardPalettenscheinAnimationFrame()
    awaitMobileYardPalettenscheinAnimationFrame()
}

private fun mobileYardDocumentAbschlussSource(auftrag: All, timestamp: Long): All =
    auftrag.copy(
        zustand = ZustandManager.ABGESCHLOSSEN,
        datum = mobileYardDocumentDate(timestamp),
        uhrzeitAbgeschlossen = mobileYardDocumentTime(timestamp).ifBlank { auftrag.uhrzeitAbgeschlossen }
    )

private fun mobileYardDocumentVorgangslog(normalizedArt: String, auftrag: All, rows: List<All>, timestamp: Long): List<All> {
    val abschlussTyp = if (normalizedArt == "WE") {
        if (mobileYardDocumentTeilabschluss(auftrag)) "WE_TEILABSCHLUSS" else "WE_ABGESCHLOSSEN"
    } else {
        if (mobileYardDocumentTeilabschluss(auftrag)) "WA_LKW_TEILABSCHLUSS" else "WA_LKW_ABGESCHLOSSEN"
    }
    val withAbschluss = if (rows.any { it.typ.equals(abschlussTyp, ignoreCase = true) || it.notiz.contains("abgeschlossen", ignoreCase = true) }) {
        rows
    } else {
        rows + mobileYardDocumentLogEntry(
            auftrag = auftrag,
            typ = abschlussTyp,
            notiz = if (abschlussTyp.endsWith("TEILABSCHLUSS")) {
                if (normalizedArt == "WE") "LKW ${auftrag.lkwkennzeichen.ifBlank { "LKW" }} abgeschlossen - Auftrag teilentladen"
                else "LKW ${auftrag.lkwkennzeichen.ifBlank { "LKW" }} abgeschlossen - Auftrag teilverladen"
            } else {
                "LKW ${auftrag.lkwkennzeichen.ifBlank { "LKW" }} abgeschlossen - Auftrag abgeschlossen"
            },
            timestamp = timestamp
        )
    }
    if (withAbschluss.any { it.typ.equals("DOKUMENTE_ERHALTEN", ignoreCase = true) || it.typ.equals("WA_DOKUMENTE_ERHALTEN", ignoreCase = true) }) {
        return withAbschluss
    }
    return withAbschluss + mobileYardDocumentLogEntry(
        auftrag = auftrag,
        typ = if (normalizedArt == "WA") "WA_DOKUMENTE_ERHALTEN" else "DOKUMENTE_ERHALTEN",
        notiz = "Dokumente erhalten",
        timestamp = timestamp + 1L
    )
}

private fun mobileYardDocumentTeilabschluss(auftrag: All): Boolean {
    val soll = auftrag.palettenanzahl
    val ist = auftrag.yardabschlussIstPaletten.takeIf { it > 0 } ?: auftrag.mobilIstPaletten.takeIf { it > 0 } ?: soll
    return soll > 0 && ist < soll
}

private fun mobileYardDocumentLogEntry(auftrag: All, typ: String, notiz: String, timestamp: Long): All =
    All(
        id = "${timestamp}_${typ}",
        typ = typ,
        notiz = notiz,
        firmenId = auftrag.firmenId,
        firma = auftrag.firma,
        lfdNr = auftrag.lfdNr,
        lieferscheinnummer = auftrag.lieferscheinnummer,
        auftragsart = auftrag.auftragsart,
        lkwkennzeichen = auftrag.lkwkennzeichen,
        fahrername = auftrag.fahrername,
        zustand = ZustandManager.ABGESCHLOSSEN,
        tor = auftrag.tor,
        torcode = auftrag.torcode,
        datum = mobileYardDocumentDate(timestamp),
        uhrzeitAnmeldung = auftrag.uhrzeitAnmeldung,
        uhrzeitTorvergabe = auftrag.uhrzeitTorvergabe,
        torZugewiesenUhrzeit = auftrag.torZugewiesenUhrzeit,
        timestamp = timestamp.toString(),
        status = ZustandManager.ABGESCHLOSSEN.toString(),
        feedArt = typ,
        feedEvent = notiz,
        feedUhrzeit = mobileYardDocumentTime(timestamp),
        feedLsNummer = auftrag.lieferscheinnummer,
        feedEventLabel = notiz
    )

private fun mobileYardDocumentTime(timestamp: Long): String {
    val date = kotlin.js.Date(timestamp.toDouble())
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}

private fun mobileYardDocumentDate(timestamp: Long): String {
    val date = kotlin.js.Date(timestamp.toDouble())
    return "${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}"
}

private suspend fun mobileYardDocumentSource(normalizedArt: String, source: All): All {
    if (normalizedArt != "WA") return source
    val kopf = runCatching {
        YardApi.loadWaListe(source.firmenId)
            .firstOrNull { it.lieferscheinnummer == source.lieferscheinnummer }
    }.getOrNull()
    val standorte = runCatching {
        YardApi.loadWaStandorteByTour(source.lieferscheinnummer, source.firmenId)
    }.getOrDefault(emptyList())
    val standort = mobileYardWaStandortForDocument(source, standorte)
    return mobileYardMergeWaAuftrag(kopf, source, standort, standorte)
}

private fun mobileYardWaStandortForDocument(lkw: All, standorte: List<All>): All? {
    val standortId = lkw.waStandortId
    if (standortId.isNotBlank()) {
        standorte.firstOrNull { it.id == standortId }?.let { return it }
    }
    lkw.platzcode.takeIf { it.isNotBlank() }?.let { platzcode ->
        standorte.firstOrNull { it.platzcode == platzcode }?.let { return it }
    }
    lkw.standort.takeIf { it.isNotBlank() }?.let { standort ->
        standorte.firstOrNull { it.standort == standort }?.let { return it }
    }
    return standorte.firstOrNull { it.typ.isNotBlank() }
}

private fun mobileYardMergeWaAuftrag(kopf: All?, lkw: All, standort: All?, standorte: List<All>): All {
    val lhmTypId = standort?.lhmTypId.orEmpty()
        .ifBlank { kopf?.lhmTypId.orEmpty() }
        .ifBlank { lkw.lhmTypId }
    val lhmTyp = standort?.slotLhmTyp.orEmpty()
        .ifBlank { standort?.typ.orEmpty() }
        .ifBlank { kopf?.slotLhmTyp.orEmpty() }
        .ifBlank { kopf?.typ.orEmpty() }
        .ifBlank { lkw.slotLhmTyp }
        .ifBlank { lkw.typ }
    val anbieterFirmenId = lkw.anbieterFirmenId
        .ifBlank { standort?.anbieterFirmenId.orEmpty() }
        .ifBlank { kopf?.anbieterFirmenId.orEmpty() }
        .ifBlank { standorte.firstNotNullOfOrNull { it.anbieterFirmenId.takeIf(String::isNotBlank) }.orEmpty() }
    return lkw.copy(
        datum = lkw.datum.ifBlank { kopf?.datum.orEmpty() },
        lieferant = lkw.lieferant.ifBlank { kopf?.lieferant.orEmpty() },
        empfaenger = lkw.empfaenger.ifBlank { kopf?.empfaenger.orEmpty() },
        auftraggeber = lkw.auftraggeber.ifBlank { kopf?.auftraggeber.orEmpty() },
        unternehmer = lkw.unternehmer.ifBlank { kopf?.unternehmer.orEmpty() },
        palettenWerte = lkw.palettenWerte.ifEmpty { kopf?.palettenWerte.orEmpty() },
        standort = standort?.standort.orEmpty().ifBlank { lkw.standort },
        ort = standort?.ort.orEmpty().ifBlank { lkw.ort },
        plz = standort?.plz.orEmpty().ifBlank { lkw.plz },
        strasse = standort?.strasse.orEmpty().ifBlank { lkw.strasse },
        hausnummer = standort?.hausnummer.orEmpty().ifBlank { lkw.hausnummer },
        anbieterFirmenId = anbieterFirmenId,
        slotLhmTyp = lhmTyp,
        lhmTypId = lhmTypId,
        typ = lhmTyp
    )
}

private suspend fun mobileYardRenderAndUploadDocument(
    root: HTMLElement,
    fileName: String,
    type: String,
    entryId: String,
    errorContext: String,
    normalizedArt: String
): Boolean {
    val pdfFile = renderLetterDocumentPdfFile(root, fileName)
    val pdfFileSize = (pdfFile.asDynamic().size as? Number)?.toDouble() ?: 0.0
    if (pdfFileSize <= 0.0) {
        console.error("Mobile Yard $errorContext PDF export failed: empty PDF", normalizedArt, entryId, fileName)
        return false
    }
    val uploadedUrl = ServiceApi.uploadServiceDocument(
        type = type,
        entryId = entryId,
        file = pdfFile,
        fileName = fileName
    )
    if (uploadedUrl.isBlank()) {
        console.error("Mobile Yard $errorContext PDF upload failed", normalizedArt, entryId, fileName)
    }
    return uploadedUrl.isNotBlank()
}

private suspend fun mobileYardSaveDocumentSnapshot(
    type: String,
    entryId: String,
    pdfFileName: String,
    dokumentTyp: String,
    layoutKey: String,
    auftrag: All,
    unternehmen: All,
    vorgangslog: List<All> = emptyList(),
    kontrollDefinitionen: List<All> = emptyList(),
    webKontrolle: All? = null,
    logoSrc: String = ""
): Boolean {
    val now = kotlin.js.Date()
    val isPalettenschein = layoutKey == LETTER_LAYOUT_PALSCHEIN_WE || layoutKey == LETTER_LAYOUT_PALSCHEIN_WA
    val aussteller = LoginService.getUser().takeIf { isPalettenschein }
    val fahrerUser = if (isPalettenschein && auftrag.slotDriverUserId.isNotBlank()) {
        val fahrerFirmenId = auftrag.anbieterFirmenId
            .ifBlank { auftrag.unternehmerFirmenId }
            .ifBlank { auftrag.auftraggeberFirmenId }
            .ifBlank { auftrag.lieferantFirmenId }
            .trim()
        LoginUserApi.loadCompanyUser(fahrerFirmenId, auftrag.slotDriverUserId)
            ?: LoginUserApi.loadUser(auftrag.slotDriverUserId)
    } else null
    val snapshot = YardDocumentSnapshot(
        dokumentTyp = dokumentTyp,
        layoutKey = layoutKey,
        erstelltAm = mobileYardDocumentDate(now.getTime().toLong()),
        erstelltUm = mobileYardDocumentTime(now.getTime().toLong()),
        auftrag = auftrag,
        unternehmen = unternehmen,
        vorgangslog = vorgangslog,
        kontrollDefinitionen = kontrollDefinitionen,
        webKontrolle = webKontrolle,
        logoSrc = logoSrc,
        ausstellerSignatur = aussteller?.signatur.orEmpty(),
        ausstellerName = aussteller?.let { listOf(it.vorname, it.nachname).filter(String::isNotBlank).joinToString(" ") }.orEmpty(),
        fahrerSignatur = fahrerUser?.signatur.orEmpty(),
        fahrerName = fahrerUser?.let { listOf(it.vorname, it.nachname).filter(String::isNotBlank).joinToString(" ") }.orEmpty()
            .ifBlank { auftrag.fahrername.ifBlank { auftrag.name } }
    )
    return ServiceApi.saveYardDocumentSnapshot(type, entryId, pdfFileName, snapshot)
}

private fun mobileYardPalettenscheinFileName(auftrag: All): String {
    val ls = auftrag.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = auftrag.lfdNr.takeIf { it > 0 }?.toString()
        ?: auftrag.id.ifBlank { "1" }
    val standort = mobileYardDocumentStandortSuffix(auftrag)
    return "${ls}-${lkw}${standort}_palettenschein.pdf"
}

private fun mobileYardProzessdokumentationFileName(auftrag: All): String {
    val ls = auftrag.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = auftrag.lfdNr.takeIf { it > 0 }?.toString()
        ?: auftrag.id.ifBlank { "1" }
    val standort = mobileYardDocumentStandortSuffix(auftrag)
    return "${ls}-${lkw}${standort}_prozessdokumentation.pdf"
}

private fun mobileYardQualitaetsdokumentationFileName(auftrag: All): String {
    val ls = auftrag.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = auftrag.lfdNr.takeIf { it > 0 }?.toString()
        ?: auftrag.id.ifBlank { "1" }
    val standort = mobileYardDocumentStandortSuffix(auftrag).replace("-", "_")
    return "${ls}_${lkw}${standort}_qualitaetsdokumentation.pdf"
}

private fun mobileYardWareneingangsberichtFileName(auftrag: All): String {
    val ls = auftrag.lieferscheinnummer.ifBlank { "auftrag" }
    val lkw = auftrag.lfdNr.takeIf { it > 0 }?.toString()
        ?: auftrag.id.ifBlank { "1" }
    val standort = mobileYardDocumentStandortSuffix(auftrag).replace("-", "_")
    return "${ls}_${lkw}${standort}_we_fehlerprotokoll.pdf"
}

private fun mobileYardDocumentStandortSuffix(auftrag: All): String =
    mobileYardDocumentStandortKey(auftrag)
        .takeIf { it.isNotBlank() }
        ?.let { "-standort-$it" }
        .orEmpty()

private fun mobileYardDocumentStandortKey(auftrag: All): String =
    auftrag.waStandortId
        .ifBlank { auftrag.slotStandortId }
        .ifBlank { auftrag.platzcode }
        .ifBlank { auftrag.standort }
        .trim()
        .filter { it.isLetterOrDigit() || it == '-' || it == '_' }

private suspend fun waitForMobileYardPalettenscheinRoot(rootId: String): HTMLElement? {
    repeat(MOBILE_YARD_PALSCHEIN_RENDER_ATTEMPTS) {
        awaitMobileYardPalettenscheinAnimationFrame()
        val root = document.getElementById(rootId) as? HTMLElement
        if (root?.querySelector("[data-letter-pdf-page='true']") != null) return root
    }
    return document.getElementById(rootId) as? HTMLElement
}

private suspend fun waitForMobileYardPalettenscheinReady(root: HTMLElement): Boolean {
    repeat(MOBILE_YARD_PALSCHEIN_RENDER_ATTEMPTS) {
        if (root.querySelector("[data-yard-driver-signature-ready='false']") == null) return true
        awaitMobileYardPalettenscheinAnimationFrame()
    }
    return root.querySelector("[data-yard-driver-signature-ready='false']") == null
}

private suspend fun awaitMobileYardPalettenscheinAnimationFrame() {
    kotlinx.coroutines.suspendCancellableCoroutine<Unit> { continuation ->
        window.requestAnimationFrame {
            continuation.resume(Unit)
        }
    }
}
